import { getEncryptedItem, setEncryptedItem } from './encryptedStorage';
import { isBrowser } from './env';
import { uuid as generateUuid } from './uuid';
import { firebaseSwUrl, getFcmWebToken, isFirebaseConfigured } from './firebase';

export type PushPermissionState = 'default' | 'denied' | 'granted';

export interface PushLocalState {
  pushDeviceId: string | null;
  pushRegistered: boolean;
  pushPermission: PushPermissionState;
  pushToken: string | null;
  pushSoundEnabled: boolean;
  pushVibrationEnabled: boolean;
  pushShowPreview: boolean;
  pushLastUpdated: number;
  pushRetryCount: number;
}

function saveState(state: PushLocalState) {
  setEncryptedItem('pushDeviceId', state.pushDeviceId);
  setEncryptedItem('pushRegistered', state.pushRegistered);
  setEncryptedItem('pushPermission', state.pushPermission);
  setEncryptedItem('pushToken', state.pushToken);
  setEncryptedItem('pushSoundEnabled', state.pushSoundEnabled);
  setEncryptedItem('pushVibrationEnabled', state.pushVibrationEnabled);
  setEncryptedItem('pushShowPreview', state.pushShowPreview);
  setEncryptedItem('pushLastUpdated', state.pushLastUpdated);
  setEncryptedItem('pushRetryCount', state.pushRetryCount);
}

export function getPushLocalState(): PushLocalState {
  return {
    pushDeviceId: getEncryptedItem<string>('pushDeviceId'),
    pushRegistered: getEncryptedItem<boolean>('pushRegistered') ?? false,
    pushPermission: (getEncryptedItem<PushPermissionState>('pushPermission') as PushPermissionState) || 'default',
    pushToken: getEncryptedItem<string | null>('pushToken'),
    pushSoundEnabled: getEncryptedItem<boolean>('pushSoundEnabled') ?? true,
    pushVibrationEnabled: getEncryptedItem<boolean>('pushVibrationEnabled') ?? true,
    pushShowPreview: getEncryptedItem<boolean>('pushShowPreview') ?? true,
    pushLastUpdated: getEncryptedItem<number>('pushLastUpdated') ?? Date.now(),
    pushRetryCount: getEncryptedItem<number>('pushRetryCount') ?? 0,
  };
}

export function updatePushLocalState(partial: Partial<PushLocalState>): PushLocalState {
  const current = getPushLocalState();
  const next = { ...current, ...partial, pushLastUpdated: Date.now() };
  saveState(next);
  syncSettingsToServiceWorker(next);
  return next;
}

export function isNotificationSupported() {
  return isBrowser() && 'Notification' in window;
}

export function isServiceWorkerSupported() {
  return isBrowser() && 'serviceWorker' in navigator;
}

export async function requestNotificationPermission(): Promise<PushPermissionState> {
  if (!isNotificationSupported()) return 'denied';
  const result = await Notification.requestPermission();
  updatePushLocalState({ pushPermission: result });
  return result;
}

export function getOrCreateDeviceId(): string {
  const existing = getEncryptedItem<string>('pushDeviceId');
  if (existing) return existing;
  const id = generateUuid('push');
  updatePushLocalState({ pushDeviceId: id });
  return id;
}

export async function initPushSystem() {
  if (!isBrowser()) return;
  const state = getPushLocalState();
  if (!state.pushDeviceId) {
    getOrCreateDeviceId();
  }
  await registerFirebaseServiceWorker();
  syncSettingsToServiceWorker(getPushLocalState());
}

/**
 * Registers the firebase-messaging service worker (config passed as query params so the SW can
 * initialize Firebase without reading env). Returns the registration, or null if unavailable.
 */
export async function registerFirebaseServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported() || !isFirebaseConfigured()) return null;
  try {
    return await navigator.serviceWorker.register(firebaseSwUrl());
  } catch (error) {
    console.error('[Push] Failed to register firebase-messaging-sw.js:', error);
    return null;
  }
}

export function enablePushLocally() {
  const permission = getPushLocalState().pushPermission;
  const granted = permission === 'granted';
  updatePushLocalState({ pushRegistered: granted });
}

export function disablePushLocally() {
  updatePushLocalState({ pushRegistered: false });
}

export function setPushSoundEnabled(enabled: boolean) {
  updatePushLocalState({ pushSoundEnabled: enabled });
}

export function setPushVibrationEnabled(enabled: boolean) {
  updatePushLocalState({ pushVibrationEnabled: enabled });
}

export function setPushShowPreview(enabled: boolean) {
  updatePushLocalState({ pushShowPreview: enabled });
}

function syncSettingsToServiceWorker(state: PushLocalState) {
  if (!isBrowser() || !navigator.serviceWorker) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.active?.postMessage({
        type: 'PUSH_SETTINGS_UPDATE',
        payload: {
          sound: state.pushSoundEnabled,
          vibration: state.pushVibrationEnabled,
          showPreview: state.pushShowPreview,
        },
      });
    })
    .catch(() => {});
}

/**
 * Obtains an FCM web registration token for this browser (Firebase web push). Registers the
 * firebase-messaging service worker and asks Firebase for a token. The returned token is what
 * gets stored on the device row and what the backend sends to via FCM HTTP v1. Idempotent —
 * Firebase returns the same token for the same browser/permission.
 * @returns FCM web token, or null when unavailable / permission not granted.
 */
export async function getWebPushToken(): Promise<string | null> {
  if (!isServiceWorkerSupported() || !isFirebaseConfigured()) return null;

  const registration = await registerFirebaseServiceWorker();
  if (!registration) return null;

  return getFcmWebToken(registration);
}
